import type { SequenceDocument } from './types';

/**
 * Erzeugt eine kanonische, stabile JSON-Darstellung einer Sequenz.
 *
 * Stabil heisst: dasselbe logische Dokument erzeugt immer denselben Text -
 * Objektschluessel werden rekursiv sortiert, Arrays behalten ihre
 * bedeutungstragende Reihenfolge (Track- und Eventreihenfolge ist absichtlich
 * bedeutungsvoll und wird nicht sortiert).
 */
export function canonicalizeSequence(doc: SequenceDocument): string {
  return stableStringify(doc, 2);
}

function stableStringify(value: unknown, indent: number): string {
  return stringifyValue(value, indent, 0);
}

function stringifyValue(value: unknown, indent: number, depth: number): string {
  if (value === undefined) {
    return 'null';
  }
  if (value === null || typeof value === 'number' || typeof value === 'boolean') {
    return JSON.stringify(value);
  }
  if (typeof value === 'string') {
    return JSON.stringify(value);
  }
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return '[]';
    }
    const inner = value
      .map((item) => pad(indent, depth + 1) + stringifyValue(item, indent, depth + 1))
      .join(',\n');
    return `[\n${inner}\n${pad(indent, depth)}]`;
  }
  if (typeof value === 'object') {
    const record = value as Record<string, unknown>;
    const keys = Object.keys(record)
      .filter((key) => record[key] !== undefined)
      .sort((a, b) => a.localeCompare(b));
    if (keys.length === 0) {
      return '{}';
    }
    const inner = keys
      .map((key) => `${pad(indent, depth + 1)}${JSON.stringify(key)}: ${stringifyValue(record[key], indent, depth + 1)}`)
      .join(',\n');
    return `{\n${inner}\n${pad(indent, depth)}}`;
  }
  throw new Error(`Kann Wert vom Typ ${typeof value} nicht kanonisch serialisieren.`);
}

function pad(indent: number, depth: number): string {
  return ' '.repeat(indent * depth);
}
